import { applyValue, inferType, type ApplyContext } from './apply.js';
import { createError, fetchContent } from './client.js';
import { createLogger } from './log.js';
import { inIframe, loadOverlay, readEditToken } from './overlay-loader.js';
import type { OverlayHandle } from './overlay-types.js';
import { resolvePageSlug } from './page.js';
import { renderRepeater } from './repeater.js';
import { KEY_ATTRIBUTE, REPEATER_ATTRIBUTE, scan } from './scan.js';
import { createState, entryFor } from './state.js';
import type {
  ApplySummary,
  ContentEntry,
  ContentEntryMap,
  ContentResponse,
  FieldType,
  SiteFlowError,
  SiteFlowHandle,
  SiteFlowOptions,
} from './types.js';

const DEFAULT_TIMEOUT_MS = 8000;
const STATE_ATTRIBUTE = 'data-siteflow-state';

/** SEO is delivered as its own block; these keys expose it like any other entry. */
function seoEntry(response: ContentResponse, key: string): ContentEntry | undefined {
  const seo = response.seo;
  if (!seo) return undefined;
  switch (key.toLowerCase()) {
    case 'seo.title':
      return seo.title == null ? undefined : { type: 'seo_title', value: seo.title };
    case 'seo.description':
      return seo.description == null ? undefined : { type: 'seo_description', value: seo.description };
    case 'seo.ogimage':
    case 'seo.og_image':
      return seo.ogImage == null ? undefined : { type: 'og_image', value: seo.ogImage };
    case 'seo.canonical':
      return seo.canonical == null ? undefined : { type: 'canonical_url', value: seo.canonical };
    default:
      return undefined;
  }
}

/**
 * Boots the SDK against the current document.
 *
 * `init` never throws and never rejects: a CMS outage on a customer's marketing
 * site must degrade to the hand-authored markup, not to a broken page. Failures
 * are reported through `hooks.onError` and the debug logger only.
 */
export function init(options: SiteFlowOptions): SiteFlowHandle {
  const hooks = options?.hooks ?? {};
  const log = createLogger(options?.debug);
  const state = createState();

  let destroyed = false;
  let overlay: OverlayHandle | null = null;
  let editToken: string | null = readEditToken();

  const report = (error: SiteFlowError): void => {
    log.error(error.message);
    try {
      hooks.onError?.(error);
    } catch (hookError) {
      log.error('onError hook threw', hookError);
    }
  };

  const notify = (name: string, run: () => void): void => {
    try {
      run();
    } catch (hookError) {
      log.warn(`${name} hook threw`, hookError);
    }
  };

  if (!options || typeof options.siteKey !== 'string' || !options.siteKey) {
    report(createError('config', 'SiteFlowCMS.init requires a siteKey'));
    return inertHandle();
  }
  if (typeof options.apiBaseUrl !== 'string' || !options.apiBaseUrl) {
    report(createError('config', 'SiteFlowCMS.init requires an apiBaseUrl'));
    return inertHandle();
  }

  const root: ParentNode = options.root ?? document;
  const slug = resolvePageSlug(typeof location === 'undefined' ? {} : location, options.page);
  const locale = options.locale;
  const fallback = options.fallbackToExistingContent !== false;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const ctx: ApplyContext = { locale: locale ?? 'en' };

  const lookup = (key: string): ContentEntry | undefined =>
    entryFor(state, key) ?? (state.response ? seoEntry(state.response, key) : undefined);

  const applyToElement = (el: Element, key: string, entry: ContentEntry): boolean => {
    const declared = (el.getAttribute('data-siteflow-type') as FieldType | null) ?? null;
    const type = entry.type ?? declared ?? inferType(el, entry.value);
    const outcome =
      type === 'repeater' || el.hasAttribute(REPEATER_ATTRIBUTE)
        ? renderRepeater(el, entry.value, ctx)
        : applyValue(el, type, entry.value, ctx);
    if (outcome === 'applied') return true;
    log.warn(`Key "${key}" did not fit <${el.tagName.toLowerCase()}>`);
    return false;
  };

  const markMissing = (el: Element): void => {
    el.setAttribute(STATE_ATTRIBUTE, 'missing');
    // With the fallback disabled the author has asked for CMS-or-nothing, so the
    // placeholder copy must not survive into production.
    if (!fallback && !el.getAttribute('data-siteflow-attr')) el.textContent = '';
  };

  const applyAll = (): ApplySummary => {
    const { fields, repeaters } = scan(root);
    state.applied.clear();
    state.missing.clear();

    for (const target of [...fields, ...repeaters]) {
      const { el, key } = target;
      const entry = lookup(key);

      if (!entry || entry.value == null) {
        state.missing.add(key);
        markMissing(el);
        continue;
      }

      // Every element is isolated: one malformed value cannot take a page down.
      try {
        if (applyToElement(el, key, entry)) state.applied.add(key);
        else state.missing.add(key);
      } catch (cause) {
        state.missing.add(key);
        log.warn(`Failed to apply "${key}"`, cause);
      }
    }

    return {
      revision: state.response?.revision ?? null,
      locale: state.response?.locale ?? ctx.locale,
      applied: Array.from(state.applied),
      missing: Array.from(state.missing),
    };
  };

  const applyEntries = (entries: ContentEntryMap): void => {
    for (const key of Object.keys(entries)) {
      const entry = entries[key];
      if (!entry) continue;
      const selector = `[${KEY_ATTRIBUTE}="${cssEscape(key)}"],[${REPEATER_ATTRIBUTE}="${cssEscape(key)}"]`;
      for (const el of Array.from(root.querySelectorAll(selector))) {
        try {
          applyToElement(el, key, entry);
        } catch (cause) {
          log.warn(`Failed to apply live edit for "${key}"`, cause);
        }
      }
    }
  };

  const maybeMountOverlay = (response: ContentResponse): void => {
    // The editor's token is the opt-in: the CMS only answers `preview: true` for a
    // token it signed, and then names the single origin allowed to drive the page.
    const editing = editToken !== null && response.preview === true && !!response.editorOrigin;
    if (overlay || !inIframe() || (!editing && options.enableEditingOverlay !== true)) return;

    const origins = editing
      ? [response.editorOrigin as string]
      : (options.allowedEditorOrigins ?? [])
          .concat(response.editorOrigin ? [response.editorOrigin] : [])
          .filter((origin, index, all) => !!origin && all.indexOf(origin) === index);

    if (!origins.length) {
      log.warn('Editing overlay requested but no editor origin is allowed');
      return;
    }

    loadOverlay(
      {
        root,
        origins,
        keys: () => scan(root).keys,
        applyEntries,
        setToken: (token) => {
          if (editToken) editToken = token;
        },
        overlayUrl: options.overlayUrl,
        debug: options.debug,
        onMounted: (handle) => {
          if (destroyed) handle.destroy();
          else overlay = handle;
        },
      },
      log,
    );
  };

  const load = async (): Promise<void> => {
    if (destroyed) return;
    if (hooks.beforeLoad) notify('beforeLoad', () => hooks.beforeLoad?.());

    try {
      let result: Awaited<ReturnType<typeof fetchContent>>;
      try {
        result = await fetchContent({
          apiBaseUrl: options.apiBaseUrl,
          siteKey: options.siteKey,
          slug,
          locale,
          timeoutMs,
          // Drafts are never cached, so a validator would only ever mislead.
          etag: editToken ? null : state.etag,
          preview: editToken,
        });
      } catch (cause) {
        // A stale or foreign token must never cost a visitor their page.
        if (editToken && isSiteFlowError(cause) && cause.status === 401) {
          editToken = null;
          return load();
        }
        throw cause;
      }

      if (destroyed) return;

      if (result.status === 304 || !result.data) {
        log.debug('Content unchanged since revision', state.response?.revision);
      } else {
        state.response = result.data;
        state.etag = result.etag ?? null;
        const loaded = result.data;
        if (hooks.afterLoad) notify('afterLoad', () => hooks.afterLoad?.(loaded));
      }

      if (!state.response) return;

      const summary = applyAll();
      log.debug(`Applied ${summary.applied.length} keys, ${summary.missing.length} missing`);
      if (hooks.onContentApplied) notify('onContentApplied', () => hooks.onContentApplied?.(summary));
      maybeMountOverlay(state.response);
    } catch (cause) {
      report(
        isSiteFlowError(cause)
          ? cause
          : createError('network', `Content load failed: ${String(cause)}`),
      );
    }
  };

  void load();

  return {
    refresh: () => load(),
    get: (key: string) => lookup(key)?.value,
    destroy: () => {
      destroyed = true;
      overlay?.destroy();
      overlay = null;
      state.response = null;
      state.etag = null;
    },
  };
}

function inertHandle(): SiteFlowHandle {
  return {
    refresh: () => Promise.resolve(),
    get: () => undefined,
    destroy: () => undefined,
  };
}

function isSiteFlowError(value: unknown): value is SiteFlowError {
  return value instanceof Error && typeof (value as SiteFlowError).code === 'string';
}

/** Minimal attribute-selector escaping; content keys are already tightly shaped. */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

export const version: string = __SDK_VERSION__;
